import React, { useEffect, useRef, useState, useCallback } from "react";
import { useMap } from "@vis.gl/react-google-maps";
// 导入你定义的类型
import { IDrawnShape, IPolygonShape, ICircleShape } from "@/util/http/Lightning/requestType"; // 假设类型定义在 ./types.ts
import { LightningColorArray, LightningSearchTypeEnum, LightningTabsEnum } from "@/util/cosntants";
import {
    LightningNewReactIcon,
    LightningSearchiCircleSelectedIcon,
    LightningSearchCircleIcon,
    LightningSearchClearPolyIcon,
    LightningProfileZoomIn,
    LightningProfileZoomOut,
} from "@/resource/IconComponents";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "@/redux/reducer/store";
import { updateNeedClearDrawMap} from "@/redux/actions/lightningAction";
import { Tooltip } from "antd";
import IF from "@/components/IF";
type DrawingMode = "none" | "polygon" | "circle";

interface IDrawingControlsProps {
  onDrawingComplete?: (shape: IDrawnShape) => void;
  initialShape?: IDrawnShape;
  searchType?: LightningSearchTypeEnum; // 新增的属性
  isMapLegendVisible?: boolean;
  currentTab: LightningTabsEnum; // 新增的属性，用于判断当前选项卡
  resetMapContent?: ()=>void
}

const strokeColor = LightningColorArray.Green; // 默认边框颜色
const strokeOpacity = 1; // 默认边框透明度
const strokeWeight = 3; // 默认边框宽度
const fillOpacity = 0.1;

const DrawingControls: React.FC<IDrawingControlsProps> = ({
    onDrawingComplete, initialShape, searchType,isMapLegendVisible,currentTab ,
    resetMapContent}) => {
    const dispatch = useDispatch();
    const map = useMap();
    const [drawingMode, setDrawingMode] = useState<DrawingMode>("none");
    const overlayRef = useRef<google.maps.Polygon | google.maps.Circle | null>(null);
    const drawingManagerRef = useRef<google.maps.drawing.DrawingManager | null>(null);
    const needClearDrawMap=useSelector((state: RootState) => state.lightningReducer?.needClearDrawMap);


    const isInternalUpdateRef = useRef(false);

    //just for mark icon selected or not-selected,need define yourself
    const [drawSelectedTrue,setDrawSelectedTrue]=useState<boolean>(true);
    const [drawSelectedFalse,setDrawSelectedFalse]=useState<boolean>(false);

    const [isClearTooltipVisible, setIsClearTooltipVisible] = useState(false);
    const [isDrawPolygonTooltipVisible, setIsDrawPolygonTooltipVisible] = useState(false);
    const [isDrawCircleTooltipVisible, setIsDrawCircleTooltipVisible] = useState(false);

    // 定义填充颜色，根据 searchType 动态计算
    const getFillColor = useCallback(():[string | undefined,number] => {
        const tmpFillColor = searchType === LightningSearchTypeEnum.ALLCLEAR ? LightningColorArray.AllClear : undefined; // 如果不是 'Risk'，则不设置 fillColor
        const tmpFillOpacity = searchType === LightningSearchTypeEnum.ALLCLEAR ? 0.2 : 0.05;
        return [tmpFillColor,tmpFillOpacity];
    }, [searchType]);

    const clearMapOverlay = useCallback(() => {
        if (overlayRef.current) {
            overlayRef.current.setMap(null);
            overlayRef.current = null;
            console.log("DrawingControls: 已清除地图上的当前图形。");
        }
    }, []);

    // 修改 drawShapeOnMap 的参数类型为 IDrawnShape (非 null 版本)
    const drawShapeOnMap = useCallback((shape: IPolygonShape | ICircleShape) => {
        if (!map) return;

        clearMapOverlay(); // 先清除任何现有图形

        let newOverlay: google.maps.Polygon | google.maps.Circle;
        const [fillColor,fillOpacity] = getFillColor(); // 获取动态填充色

        if (shape.type === "polygon") {
            const data = shape.data as google.maps.LatLngLiteral[];
            newOverlay = new google.maps.Polygon({
                paths: data,
                strokeColor,
                strokeOpacity,
                strokeWeight,
                fillColor: fillColor, // 使用动态填充色
                fillOpacity: fillOpacity,
                editable: true,
                map: map,
            });
            console.log("DrawingControls: 重新绘制多边形。");

            // 现有事件监听器保持不变
            google.maps.event.addListener(newOverlay.getPath(), "set_at", () => {
                if (onDrawingComplete) {
                    isInternalUpdateRef.current = true;
                    onDrawingComplete({
                        type: "polygon",
                        data: (newOverlay as google.maps.Polygon).getPath().getArray().map(point => ({ lat: point.lat(), lng: point.lng() }))
                    });
                }
            });
            google.maps.event.addListener(newOverlay.getPath(), "insert_at", () => {
                if (onDrawingComplete) {
                    isInternalUpdateRef.current = true;
                    onDrawingComplete({
                        type: "polygon",
                        data: (newOverlay as google.maps.Polygon).getPath().getArray().map(point => ({ lat: point.lat(), lng: point.lng() }))
                    });
                }
            });
            google.maps.event.addListener(newOverlay.getPath(), "remove_at", () => {
                if (onDrawingComplete) {
                    isInternalUpdateRef.current = true;
                    onDrawingComplete({
                        type: "polygon",
                        data: (newOverlay as google.maps.Polygon).getPath().getArray().map(point => ({ lat: point.lat(), lng: point.lng() }))
                    });
                }
            });

        } else if (shape.type === "circle") {
            const data = shape.data as (google.maps.LatLngLiteral & { radiusKm: number });
            newOverlay = new google.maps.Circle({
                center: { lat: data.lat, lng: data.lng },
                radius: data.radiusKm * 1000,
                strokeColor,
                strokeOpacity,
                strokeWeight,
                fillColor: fillColor, // 使用动态填充色
                fillOpacity: fillOpacity,
                editable: true,
                map: map,
            });
            console.log("DrawingControls: 重新绘制圆形。");

            // 现有事件监听器保持不变
            google.maps.event.addListener(newOverlay, "center_changed", () => {
                if (onDrawingComplete) {
                    isInternalUpdateRef.current = true;
                    onDrawingComplete({
                        type: "circle",
                        data: {
                            lat: (newOverlay as google.maps.Circle).getCenter()?.lat() || 0,
                            lng: (newOverlay as google.maps.Circle).getCenter()?.lng() || 0,
                            radiusKm: (newOverlay as google.maps.Circle).getRadius() / 1000,
                        }
                    });
                }
            });
            google.maps.event.addListener(newOverlay, "radius_changed", () => {
                if (onDrawingComplete) {
                    isInternalUpdateRef.current = true;
                    onDrawingComplete({
                        type: "circle",
                        data: {
                            lat: (newOverlay as google.maps.Circle).getCenter()?.lat() || 0,
                            lng: (newOverlay as google.maps.Circle).getCenter()?.lng() || 0,
                            radiusKm: (newOverlay as google.maps.Circle).getRadius() / 1000,
                        }
                    });
                }
            });
        } else {
            return;
        }

        overlayRef.current = newOverlay;

    }, [map, clearMapOverlay, onDrawingComplete, getFillColor]); // 依赖项中添加 getFillColor


    // --- Effect 1: 初始化 DrawingManager 和设置事件监听器 ---
    // 这个 useEffect 负责 DrawingManager 的生命周期管理
    useEffect(() => {
        if (!map) {
            console.log("DrawingControls: 地图实例未准备好，无法初始化 DrawingManager。");
            return;
        }
        // 确保只初始化一次 DrawingManager
        if (drawingManagerRef.current) {
            console.log("DrawingControls: DrawingManager 已存在，跳过重新初始化。");
            return;
        }

        console.log("DrawingControls: 正在初始化 DrawingManager...");
        const [fillColor,fillOpacity] = getFillColor(); // 获取动态填充色
        const drawingManager = new google.maps.drawing.DrawingManager({
            drawingMode: null, // 初始不激活任何绘图模式
            map,
            drawingControl: false, // 不显示默认的绘图工具条
            polygonOptions: {
                strokeColor,
                strokeOpacity,
                strokeWeight,
                fillColor: fillColor, // 初始化时设置 fillColor
                fillOpacity: fillOpacity,
                editable: true,
            },
            circleOptions: {
                strokeColor,
                strokeOpacity,
                strokeWeight,
                fillColor: fillColor, // 初始化时设置 fillColor
                fillOpacity: fillOpacity,
                editable: true,
            },
        });

        drawingManagerRef.current = drawingManager;

        const overlaycompleteListener = google.maps.event.addListener(
            drawingManager,
            "overlaycomplete",
            (event: any) => {
                clearMapOverlay(); // 绘制完成时清除之前的图形，确保只有一个
                isInternalUpdateRef.current = true; // 标记为内部更新

                overlayRef.current = event.overlay; // 保存新绘制的图形

                drawingManager.setDrawingMode(null); // 绘制完成后禁用绘图模式
                setDrawingMode("none"); // 更新组件内部状态

                let completedShape: IDrawnShape = null;

                if (event.type === google.maps.drawing.OverlayType.POLYGON) {
                    const polygon = event.overlay as google.maps.Polygon;
                    completedShape = {
                        type: "polygon",
                        data: polygon.getPath().getArray().map(point => ({ lat: point.lat(), lng: point.lng() }))
                    };
                    console.log("DrawingControls: 用户绘制多边形完成:", completedShape.data);

                    // 监听多边形路径修改事件
                    google.maps.event.addListener(polygon.getPath(), "set_at", () => {
                        if (onDrawingComplete) {
                            isInternalUpdateRef.current = true;
                            onDrawingComplete({ type: "polygon", data: polygon.getPath().getArray().map(point => ({ lat: point.lat(), lng: point.lng() })) });
                        }
                    });
                    google.maps.event.addListener(polygon.getPath(), "insert_at", () => {
                        if (onDrawingComplete) {
                            isInternalUpdateRef.current = true;
                            onDrawingComplete({ type: "polygon", data: polygon.getPath().getArray().map(point => ({ lat: point.lat(), lng: point.lng() })) });
                        }
                    });
                    google.maps.event.addListener(polygon.getPath(), "remove_at", () => {
                        if (onDrawingComplete) {
                            isInternalUpdateRef.current = true;
                            onDrawingComplete({ type: "polygon", data: polygon.getPath().getArray().map(point => ({ lat: point.lat(), lng: point.lng() })) });
                        }
                    });

                } else if (event.type === google.maps.drawing.OverlayType.CIRCLE) {
                    const circle = event.overlay as google.maps.Circle;
                    const center = circle.getCenter();
                    const radiusMeters = circle.getRadius();
                    completedShape = {
                        type: "circle",
                        data: {
                            lat: center?.lat() || 0,
                            lng: center?.lng() || 0,
                            radiusKm: radiusMeters / 1000,
                        }
                    };
                    console.log("DrawingControls: 用户绘制圆形完成:", completedShape.data);

                    // 监听圆形中心和半径修改事件
                    google.maps.event.addListener(circle, "center_changed", () => {
                        if (onDrawingComplete) {
                            isInternalUpdateRef.current = true;
                            onDrawingComplete({
                                type: "circle",
                                data: {
                                    lat: circle.getCenter()?.lat() || 0,
                                    lng: circle.getCenter()?.lng() || 0,
                                    radiusKm: circle.getRadius() / 1000,
                                }
                            });
                        }
                    });
                    google.maps.event.addListener(circle, "radius_changed", () => {
                        if (onDrawingComplete) {
                            isInternalUpdateRef.current = true;
                            onDrawingComplete({
                                type: "circle",
                                data: {
                                    lat: circle.getCenter()?.lat() || 0,
                                    lng: circle.getCenter()?.lng() || 0,
                                    radiusKm: circle.getRadius() / 1000,
                                }
                            });
                        }
                    });
                }

                if (onDrawingComplete) {
                    onDrawingComplete(completedShape); // 直接传递 IDrawnShape 对象
                }
            }
        );

        // 清理函数：组件卸载时移除 DrawingManager 和监听器
        return () => {
            console.log("DrawingControls: 正在清理 DrawingManager...");
            if (overlaycompleteListener) {
                google.maps.event.removeListener(overlaycompleteListener);
            }
            if (drawingManagerRef.current) {
                // clearMapOverlay(); // 清除地图上的绘制图形，effect currentTab 会处理
                drawingManagerRef.current.setMap(null); // 将 DrawingManager 从地图上移除
                drawingManagerRef.current = null; // 清空引用
            }
        };
    }, [map, onDrawingComplete, clearMapOverlay, getFillColor]); // 依赖项中添加 getFillColor

    // --- Effect 2: 根据 drawingMode 切换 DrawingManager 的绘图模式 ---
    // 这个 useEffect 响应 drawingMode 状态变化来控制绘图工具
    useEffect(() => {
        if (!drawingManagerRef.current) {
            console.log("DrawingControls: DrawingManager 未准备好，无法设置绘图模式。");
            return;
        }

        if (drawingMode !== "none") {
            // clearMapOverlay(); // 切换模式时清除当前图形
            console.log(`DrawingControls: 设置绘图模式为 ${drawingMode === "polygon" ? "多边形" : "圆形"}。`);
            drawingManagerRef.current.setDrawingMode(
                drawingMode === "polygon" ? google.maps.drawing.OverlayType.POLYGON : google.maps.drawing.OverlayType.CIRCLE
            );
        } else {
            console.log("DrawingControls: 设置绘图模式为无。");
            drawingManagerRef.current.setDrawingMode(null);
        }
    }, [drawingMode]);

    // --- Effect 3: 监听 initialShape prop 变化并绘制 ---
    // 这个 useEffect 负责处理外部传入的 initialShape prop
    useEffect(() => {
        if (isInternalUpdateRef.current) {
            console.log("DrawingControls: initialShape 变化来自内部更新，跳过重绘。");
            isInternalUpdateRef.current = false; // 重置标记
            return;
        }

        if(currentTab === LightningTabsEnum.DRAWONMAP){
            if (map && initialShape) {
                console.log("DrawingControls: initialShape prop 发生变化（外部更新），尝试绘制历史图形。", initialShape);
                drawShapeOnMap(initialShape as IPolygonShape | ICircleShape); // 明确类型断言
            } else if (map && initialShape === null ) {
                console.log("DrawingControls: initialShape 为 null（外部更新），清除当前绘制。");
                clearMapOverlay();
            }
        }
        else{
            console.log("DrawingControls: 当前选项卡不是 DRAWONMAP，跳过绘制。");
            clearMapOverlay(); // 如果不是 DRAWONMAP，则清除地图上的图形
            setDrawingMode("none"); // 禁用绘图模式
            if (drawingManagerRef.current) {
                drawingManagerRef.current.setDrawingMode(null); // 确保 DrawingManager 也被禁用
            }
        }

    }, [initialShape, map, drawShapeOnMap, clearMapOverlay,currentTab]); // 依赖项中添加 drawShapeOnMap, clearMapOverlay

    useEffect(()=>{
        const [fillColor,fillOpacity] = getFillColor(); // 获取动态填充色
        if(overlayRef.current && map){
            // 如果 overlayRef.current 已经存在，则更新其属性
            // const fillColor = getFillColor();
            overlayRef.current.setOptions({fillColor: fillColor});
            console.log("DrawingControls: 更新现有图形的填充颜色。", fillColor);
        }
        if(drawingManagerRef.current && map){
            // 如果 drawingManagerRef.current 已经存在，则更新其属性
            drawingManagerRef.current.setOptions({
                polygonOptions: {
                    strokeColor,
                    strokeOpacity,
                    strokeWeight,
                    fillColor: fillColor, // 初始化时设置 fillColor
                    fillOpacity: fillOpacity,
                    editable: true,
                },
                circleOptions: {
                    strokeColor,
                    strokeOpacity,
                    strokeWeight,
                    fillColor: fillColor, // 初始化时设置 fillColor
                    fillOpacity: fillOpacity,
                    editable: true
                },
            });
        }
    },[currentTab,searchType,getFillColor]);
    useEffect(()=>{
        if(needClearDrawMap && map){
            handleClearButtonClick();
        }
    },[needClearDrawMap]);

    // --- 事件处理函数 ---
    // 这些函数控制 drawingMode 状态
    const handleDrawPolygon = () => {
        clearMapOverlay();
        setDrawingMode(prevMode => {
            const newMode = (prevMode === "polygon" ? "none" : "polygon");
            if (newMode !== "none") {
                isInternalUpdateRef.current = false; // 准备接受新的用户绘制
            }
            // 如果切换到绘制模式，通知父组件清空 currentDrawnShape, 以便重新开始
            if(onDrawingComplete && newMode !== "none"){
                onDrawingComplete(null);
            }
            return newMode;
        });
        resetMapContent();
    };

    const handleDrawCircle = () => {
        clearMapOverlay();
        setDrawingMode(prevMode => {
            const newMode = (prevMode === "circle" ? "none" : "circle");
            if (newMode !== "none") {
                isInternalUpdateRef.current = false; // 准备接受新的用户绘制
            }
            if(onDrawingComplete && newMode !== "none"){
                onDrawingComplete(null);
            }
            return newMode;
        });
        resetMapContent();
    };

    const handleClearButtonClick = () => {
        clearMapOverlay(); // 清除地图上的图形
        setDrawingMode("none"); // 禁用绘图模式
        if (onDrawingComplete) {
            isInternalUpdateRef.current = true; // 标记为内部清除操作
            onDrawingComplete(null); // 通知父组件已清除
        }

        dispatch(updateNeedClearDrawMap(false));
    };

    // 如果地图实例还未准备好，不渲染任何 UI
    if (!map) return null;
    if (currentTab !== LightningTabsEnum.DRAWONMAP) {
        // 如果当前选项卡不是 DRAWONMAP，则不渲染绘图控件
        return null;
    }

    return (
        <div style={{
            position: "absolute",
            top: `${searchType === LightningSearchTypeEnum.ALLCLEAR ? "90px" : (
                searchType === LightningSearchTypeEnum.DETECTED ? "60px" : "60px"
            )}`, // 根据 searchType 控制位置
            left: "20px",
            display: `${isMapLegendVisible ? "none" : "flex"}`, // 根据 isMapLegendVisible 控制显示
            flexDirection: "column",
            zIndex: 1000,
            borderRadius: "4px",
        }}>
            <Tooltip title="Draw Polygon" placement="left" visible={isDrawPolygonTooltipVisible}>
                <button
                    onClick={handleDrawPolygon}
                    style={{
                        borderRadius: "4px",
                        cursor: "pointer",
                        fontWeight: "bold",
                        color: "#333",
                        transition: "background-color 0.2s",
                    }}
                    onMouseEnter={() => setIsDrawPolygonTooltipVisible(true)}
                    onMouseLeave={() => setIsDrawPolygonTooltipVisible(false)}
                >
                    <LightningNewReactIcon selected={drawingMode === "polygon"} />
                </button>
            </Tooltip>
            <Tooltip title="Draw Circle" placement="left" visible={isDrawCircleTooltipVisible}>
                <button
                    onClick={handleDrawCircle}
                    style={{
                        borderRadius: "4px",
                        cursor: "pointer",
                        fontWeight: "bold",
                        color: "#333",
                        transition: "background-color 0.2s",
                    }}
                    onMouseEnter={() => setIsDrawCircleTooltipVisible(true)}
                    onMouseLeave={() => setIsDrawCircleTooltipVisible(false)}
                >
                    <IF show={drawingMode === "circle" }>
                        <LightningSearchiCircleSelectedIcon />
                    </IF>
                    <IF show={!(drawingMode === "circle")}>
                        <LightningSearchCircleIcon />
                    </IF>
                </button>
            </Tooltip>
            <Tooltip title="Clear" placement="left" visible={isClearTooltipVisible}>
                <button
                    onClick={handleClearButtonClick}
                    style={{
                        borderRadius: "4px",
                        cursor: "pointer",
                        fontWeight: "bold",
                        color: "#d9534f",
                        transition: "background-color 0.2s",
                    }}
                    onMouseEnter={() => setIsClearTooltipVisible(true)}
                    onMouseLeave={() => setIsClearTooltipVisible(false)}
                >
                    <LightningSearchClearPolyIcon />
                </button>
            </Tooltip>
        </div>
    );
};

export default React.memo(DrawingControls);